// eslint-disable-next-line no-unused-vars
import axios, { AxiosRequestConfig } from "axios";
import { Cognito } from "./Cognito";
import { appConfig } from "./AppConfig";
import { appCertification } from "./AppCertification";
import { dateTimeHelper } from "./DateTimeHelper";
import { v4 as uuidv4 } from "uuid";
import qs from "qs";
import { commonFunction } from "./CommonFunction";

import { i18n } from "@/lang/lang.js";
/**
 * axiosとCognitoアクセスをラップした専用クラスです。
 */
export default class HttpClient {
  /**
   * コンストラクタ
   * @param {String} baseURL
   */
  constructor() {
    axios.defaults.timeout = 60000;
    axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
    axios.defaults.baseURL = appConfig.APP_CONFIG.BASE_URL;
    this.cognito = new Cognito();
  }

  // バージョンエラーステータスリスト
  versionErrorStatusList = [
    // 最新バージョンのPC画面利用不可
    "LatestVersionPCScreenUnavailable",
    // 最新バージョンのPC画面リリース中
    "LatestVersionPCScreenReleasing",
    // PC画面のバージョン不一致
    "PCScreenVersionMismatch",
    // 最新バージョンのAPI利用不可
    "LatestVersionAPIUnavailable",
    // 最新バージョンのAPIリリース中
    "LatestVersionAPIReleasing",
    // APIのバージョン不一致
    "APIVersionMismatch",
  ];

  // MEMO 外部ライブラリで頻繁に使用され、
  // 不要な解決がされる可能性が高いため、configという変数は避けてください。

  /**
   * HTTPメソッド定数定義
   */
  Method = {
    /**
     * GET
     */
    GET: "GET",
    /**
     * POST
     */
    POST: "POST",
  };

  /**
   * BaseURLを設定します。
   * @param {String} defaultsBaseURL
   */
  setDefaultsBaseURL(defaultsBaseURL) {
    axios.defaults.baseURL = defaultsBaseURL;
  }

  /**
   * URLの末尾にあるスラッシュを削除します。
   * @param {String} url
   */
  removeUrlEndSlash(url) {
    return url.replace(/\/$/, "");
  }

  /**
   * 現時点で有効なヘッダーを設定します。
   * @param {AxiosRequestConfig} _config
   */
  setHeader(_config) {
    return new Promise((resolve, reject) => {
      if (_config === undefined) {
        _config = {};
      }

      // axios問題で配列クエリパラメータのシリアライズフォーマット修正
      const paramsSerializer = (params) =>
        qs.stringify(params, { arrayFormat: "indices", allowDots: true });
      _config.paramsSerializer = paramsSerializer;

      // APIアクセスであれば常にトークンを更新して設定します。
      if (
        _config.baseURL == undefined ||
        _config.baseURL == appConfig.APP_CONFIG.BASE_URL ||
        (axios.defaults.baseURL == appConfig.APP_CONFIG.BASE_URL && _config.baseURL == undefined)
      ) {
        this.cognito
          .getIdToken()
          .then((idToken) => {
            _config.headers = { Authorization: idToken };
            resolve(_config);
          })
          .catch((error) => {
            console.error("setHeader error", error);
            this.logout();
            console.error("認証の有効期限が切れました。再ログインが必要です。");
            reject(error);
            // ログイン画面へ遷移
            window.location.replace("/");
          });
      } else {
        resolve(_config);
      }
    });
  }

  /**
   * axios getのラッパーメソッドです。
   * @param {String} uri
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<AxiosResponse<any>>}
   */
  async get(uri, _config) {
    const newConfig = await this.setHeader(_config);
    return axios.get(uri, newConfig);
  }

  /**
   * axios postのラッパーメソッドです。
   * @param {String} uri
   * @param {Json} data
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<AxiosResponse<any>>}
   */
  async post(uri, data, _config) {
    const newConfig = await this.setHeader(_config);
    return axios.post(uri, data, newConfig);
  }

  /**
   * axios putのラッパーメソッドです。
   * @param {String} uri
   * @param {Json} data
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<AxiosResponse<any>>}
   */
  async put(uri, data, _config) {
    const newConfig = await this.setHeader(_config);
    return axios.put(uri, data, newConfig);
  }

  /**
   * username, passwordでログインします。
   * @param {String} userId
   * @param {String} password
   * @returns {Promise<any>}
   */
  login(userId, password) {
    return this.cognito.login(userId, password);
  }

  /**
   * Refresh Token でログイン状態にします。
   */
  createNewLoginSession(userId, refreshToken) {
    return this.cognito.createNewLoginSession(userId, refreshToken);
  }

  /**
   * ログアウトします。
   * @param {String} userId
   */
  logout(userId) {
    this.cognito.logout(userId);
  }

  /**
   * トークンをクリアします。
   */
  clearToken() {
    this.cognito.clearToken();
  }

  /**
   * credentialsを取得します。
   * ※ApiGatewayを並列実行したい場合にcredentialsを更新するために呼びます。
   * @returns credentials
   *
   */
  getApiGatewayCredentials() {
    return this.cognito.getIdToken().then((idToken) => {
      return this.cognito.makeCredentials(idToken);
    });
  }
  /**
   * ユーザーの属性を取得します。
   * @param {String} userId
   * @returns {Promise<any>}
   */
  getUserAttribute(userId) {
    return this.cognito.getUserAttribute(userId);
  }

  /**
   * ユーザーのパスワードを変更します
   * @param {String} userId
   * @param {String} oldPassword
   * @param {String} newPassword
   */
  changePassword(userId, oldPassword, newPassword) {
    return this.cognito.changePassword(userId, oldPassword, newPassword);
  }

  /**
   * GetAPIリクエスト用のConfigを作成します。
   * @returns {AxiosRequestConfig}
   */
  createGetApiRequestConfig() {
    const _config = {};

    _config.params = {
      reqComDeviceImei: "WEB", //ブランク
      reqComAppVersion: appCertification.APPVERSION, //ブランク
      reqComExecUser: sessionStorage.getItem("usercode"),
      reqComExecTimestamp: dateTimeHelper.convertUTC(),
      reqComRequestId: uuidv4(),
      reqComPaginationFlg: "0",
      reqComPageIndex: "1",
      reqComPageLimit: "100",
      reqComLanguageCd: sessionStorage.getItem("lang"),
      reqComReferenceDate: dateTimeHelper.convertUTC(),
      reqComSortItem: "",
      reqComTimeZone: dateTimeHelper.getTimeZone(),
      reqComCustomerDiv: sessionStorage.getItem("userInfo.customerDiv"),
    };

    return _config;
  }
  /**
   * PostAPIリクエスト用のConfigを作成します。
   * @returns {AxiosRequestConfig}
   */
  createPostApiRequestConfig() {
    const _config = {};
    //const language = window.navigator.language;
    //const lang = language.toLowerCase().split("-");

    _config.params = {
      // reqComLanguageCd: lang[0],
      MessageGroupId: "",
    };
    return _config;
  }

  /**
   * PostAPIリクエスト用のConfigを作成します。
   * @returns
   */
  createRequestBodyConfig() {
    const body = {};
    let kvp = {};
    kvp.reqComComponentId = "";
    kvp.reqComUserSid = sessionStorage.getItem("userInfo.userSid");
    kvp.reqComDeviceImei = "WEB";
    kvp.reqComAppVersion = appCertification.APPVERSION;
    kvp.reqComExecUser = sessionStorage.getItem("usercode");
    kvp.reqComExecTimestamp = dateTimeHelper.convertUTC();
    kvp.reqComRequestId = uuidv4();
    kvp.reqComLanguageCd = sessionStorage.getItem("lang");
    kvp.reqComTimeZone = dateTimeHelper.getTimeZone();
    kvp.reqComCustomerDiv = sessionStorage.getItem("userInfo.customerDiv");
    body.reqCom = kvp;
    body.reqIdv = {};
    return body;
  }

  /**
   * API GatewayのGetリクエストです。
   * @param {String} uri
   * @param {any} data
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<R>}
   */
  async secureGet(uri, _config) {
    const newConfig = await this.setHeader(_config);
    return axios
      .get(uri, newConfig)
      .then((response) => {
        // ログ出力
        // console.info("response", response);
        // バージョンエラーステータスリスト
        const versionErrorStatusList = [
          // 最新バージョンのPC画面利用不可
          "LatestVersionPCScreenUnavailable",
          // 最新バージョンのPC画面リリース中
          "LatestVersionPCScreenReleasing",
          // PC画面のバージョン不一致
          "PCScreenVersionMismatch",
          // 最新バージョンのAPI利用不可
          "LatestVersionAPIUnavailable",
          // 最新バージョンのAPIリリース中
          "LatestVersionAPIReleasing",
          // APIのバージョン不一致
          "APIVersionMismatch",
        ];

        // レスポンスステータス
        const resComStatus = response.data.resCom.resComStatus;
        // バージョンエラーに該当する場合
        if (versionErrorStatusList.includes(resComStatus)) {
          // ログアウトする
          this.logout();
          // ログ出力
          // console.info("logout");
          // エラーメッセージ表示
          alert(response.data.resCom.resComMessage);
          // PC画面のバージョン不一致の場合
          if (resComStatus == "PCScreenVersionMismatch") {
            // キャッシュをクリアする
            window.location.reload(true);
          }
          // ログイン画面へ遷移
          window.location.href = "/";
        }
        // responseを返す
        return response;
      })
      .catch((ex) => {
        console.error(ex);
        throw ex;
      });
  }

  /**
   * API GatewayのPostリクエストです。
   * @param {String} uri
   * @param {any} data
   * @param {AxiosRequestConfig} _config
   * @returns {Promise<R>}
   */
  async securePost(uri, data, _config) {
    const newConfig = await this.setHeader(_config);
    return axios
      .post(uri, data, newConfig)
      .then((response) => {
        // ログ出力
        // console.info("response", response);
        // バージョンエラーステータスリスト
        const versionErrorStatusList = [
          // 最新バージョンのPC画面利用不可
          "LatestVersionPCScreenUnavailable",
          // 最新バージョンのPC画面リリース中
          "LatestVersionPCScreenReleasing",
          // PC画面のバージョン不一致
          "PCScreenVersionMismatch",
          // 最新バージョンのAPI利用不可
          "LatestVersionAPIUnavailable",
          // 最新バージョンのAPIリリース中
          "LatestVersionAPIReleasing",
          // APIのバージョン不一致
          "APIVersionMismatch",
        ];

        // レスポンスステータス
        const resComStatus = response.data.resCom.resComStatus;
        // バージョンエラーに該当する場合
        if (versionErrorStatusList.includes(resComStatus)) {
          // ログアウトする
          this.logout();
          // ログ出力
          // console.info("logout");
          // エラーメッセージ表示
          alert(response.data.resCom.resComMessage);
          // PC画面のバージョン不一致の場合
          if (resComStatus == "PCScreenVersionMismatch") {
            // キャッシュをクリアする
            window.location.reload(true);
          }
          // ログイン画面へ遷移
          window.location.href = "/";
        }
        // responseを返す
        return response;
      })
      .catch((ex) => {
        console.error(ex);
        throw ex;
      });
  }

  /**
   * API Gateway 30秒タイムアウトを回避するための Step Functions 統合処理の GETバージョンです。
   * @param path Step Functions を経由してAPIサーバに渡すURLのパス
   * @param params Step Functions を経由してAPIサーバに渡す（queryString に変換する前の）パラメータ
   * @returns Step Functions を経由して実行したAPIのレスポンス
   */
  async stepFunctionsGetAsync(path, params) {
    try {
      const pathPattern = path.split("/")[0];
      const apiUrl =
        pathPattern === "reporting"
          ? sessionStorage.getItem("AWS.REPORTINGAPIURL")
          : pathPattern === "business-g3"
          ? sessionStorage.getItem("AWS.APIG3URL")
          : sessionStorage.getItem("AWS.APIURL");

      const paramString = new URLSearchParams(params).toString();

      const inputValue = {
        apiUrl: apiUrl + path + "?" + paramString,
        body: "",
        method: this.Method.GET,
      };

      return await this.stepFunctionsFetchAsync(path, inputValue);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * API Gateway 30秒タイムアウトを回避するための Step Functions 統合処理の POSTバージョンです。
   * @param path Step Functions を経由してAPIサーバに渡すURLのパス
   * @param body Step Functions を経由してAPIサーバに渡す Body
   * @param skipResult 非同期処理を待たない用のフラグ
   * - true：非同期処理を待たない
   * - false：非同期処理を待つ（デフォルト）
   * @returns Step Functions を経由して実行したAPIのレスポンス
   */
  async stepFunctionsPostAsync(path, body, skipResult = false) {
    try {
      const pathPattern = path.split("/")[0];
      const apiUrl =
        pathPattern === "reporting"
          ? sessionStorage.getItem("AWS.REPORTINGAPIURL")
          : pathPattern === "business-g3"
          ? sessionStorage.getItem("AWS.APIG3URL")
          : sessionStorage.getItem("AWS.APIURL");

      const inputValue = {
        apiUrl: apiUrl + path,
        body: body,
        method: this.Method.POST,
      };

      return await this.stepFunctionsFetchAsync(path, inputValue, skipResult);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * @param path Step Functions を経由してAPIサーバに渡すURLのパス
   * @param inputValue Step Functions を経由してAPIサーバに渡すインプット
   * @param skipResult 非同期処理を待たない用のフラグ
   * - true：非同期処理を待たない
   * - false：非同期処理を待つ（デフォルト）
   * @returns Step Functions を経由して実行したAPIのレスポンス
   */
  async stepFunctionsFetchAsync(path, inputValue, skipResult = false) {
    const startExecutionUrl = sessionStorage.getItem("AWS.STARTEXECUTIONURL");
    const stateMachineArn = sessionStorage.getItem("AWS.STATEMACHINEARN");
    const dateKey = dateTimeHelper.convertUTC().replace(/[^0-9]/g, "");
    const executionName = dateKey + "_" + path.replace(/[^0-9a-zA-Z]/g, "");

    const requestValue = {
      input: JSON.stringify(inputValue),
      name: executionName.length > 80 ? executionName.substring(0, 80) : executionName,
      stateMachineArn: stateMachineArn,
    };

    const executionArn = await this.startExecutionAsync(startExecutionUrl, requestValue);

    const describeExecutionUrl = sessionStorage.getItem("AWS.STARTEXECUTIONURL") + "/status";
    const delay = 2000;
    let response = null;

    if (skipResult) {
      return;
    }

    do {
      await new Promise((resolve) => setTimeout(resolve, delay));
      response = await this.describeExecutionAsync(executionArn, describeExecutionUrl);
    } while (response && response.status === "RUNNING");

    if (response.status.toUpperCase() !== "SUCCEEDED") {
      switch (response.error) {
        case appConfig.STEP_FUNCTIONS_STATES_ERRORS.DATA_LIMIT_EXCEEDED:
          throw new Error(i18n.t("error.STEP_FUNCTIONS_STATES_ERRORS_DATA_LIMIT_EXCEEDED"));
        case appConfig.STEP_FUNCTIONS_STATES_ERRORS.TIMEOUT:
          throw new Error(i18n.t("error.STEP_FUNCTIONS_STATES_ERRORS_TIMEOUT"));
        default:
          throw new Error(i18n.t("error.err_asyncProcess"));
      }
    }

    return response.output;
  }

  /**
   * API Gateway のアクション：startExecution をコールします。
   * @param startExecutionUrl startExecution の URL
   * @param requestValue Step Functions に渡すインプット
   * @returns startExecution の Arn
   */
  async startExecutionAsync(startExecutionUrl, requestValue) {
    const idToken = await this.cognito.getIdToken();

    const response = await fetch(startExecutionUrl, {
      method: "POST",
      mode: "cors",
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestValue),
    });

    if (!response.ok) {
      // TODO: エラーメッセージの定義等のエラーハンドリング
      throw new Error(i18n.t("error.err_asyncProcess"));
    }

    const jsonData = await response.json();

    return jsonData.executionArn;
  }

  /**
   * API Gateway のアクション：describeExecution をコールして非同期処理の進捗状況を確認します。
   * @param executionArn する 進捗を確認する非同期処理の Arn
   * @param describeExecutionUrl describeExecution の URL
   * @returns 非同期処理の進捗状況および実行結果
   */
  async describeExecutionAsync(executionArn, describeExecutionUrl) {
    const idToken = await this.cognito.getIdToken();

    const body = {
      executionArn: executionArn,
    };

    const response = await fetch(describeExecutionUrl, {
      method: "POST",
      mode: "cors",
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return await response.json();
  }

  /**
   * S3 PutObject 用の署名付きURLを取得します。返却される値は以下の形式の Json になります。
   *   body.presignedUrl;
   *   body.fileKey;
   */
  async getPutObjectSignedUrl() {
    try {
      const idToken = await this.cognito.getIdToken();
      const path = "/business/a-issue-presigned-url";
      const apiUrl = path.startsWith("/") ? path : "/" + path;
      const baseUrl = this.removeUrlEndSlash(appConfig.APP_CONFIG.BASE_URL);
      const url = baseUrl + apiUrl;
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: { Authorization: idToken },
      });
      const jsonData = await response.json();
      return JSON.parse(jsonData.body);
    } catch (ex) {
      console.log(ex);
    }
  }

  /**
   * ユーザーIDとパスワードでCognito認証した結果をCookieに記録するAPIをコールします。
   * @param userId
   * @param password
   * @returns APIの実行結果
   */
  async initiateAuth(userId, password) {
    try {
      const apiUrl = "/auth/initiate";
      const requestData = { userName: userId, password: password };
      return await this.lambdaUrlPostAsync(apiUrl, requestData);
    } catch (error) {
      console.error(error);
      return error;
    }
  }

  /**
   * HttpOnly 属性の Cookie に設定されたリフレッシュトークンをAPIサーバを介して取得します。
   * @returns リフレッシュトークン
   */
  async getRefreshToken() {
    try {
      const apiUrl = "/auth/get-id-token";
      const requestData = { mode: "current" };
      return await this.lambdaUrlPostAsync(apiUrl, requestData);
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }

  /**
   * HttpOnly 属性の Cookie を無効（期限切れ）に設定します。
   */
  async setCookieExpired() {
    try {
      const apiUrl = "/auth/set-cookie-expired";
      const requestData = { mode: "all" };
      await this.lambdaUrlPostAsync(apiUrl, requestData);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Lambda Functions URL へのPOSTリクエストを実行して結果を返します。
   * @param path APIの相対パス
   * @param body Body
   * @returns APIの実行結果
   */
  async lambdaUrlPostAsync(path, body) {
    if (!path || path.length < 1) return null;

    //const baseUrl = "https://fg-cirrus.nikkon-is.com";
    const baseUrl = this.removeUrlEndSlash(appConfig.APP_CONFIG.BASE_URL);
    const apiUrl = path.startsWith("/") ? path : "/" + path;
    const url = baseUrl + apiUrl;
    const bodyData = JSON.stringify(body);
    const bodyDataHash = await this.getSha256HashValue(bodyData);
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("x-amz-content-sha256", bodyDataHash);

    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      headers: headers,
      body: bodyData,
    });

    if (!response.ok) return undefined;

    return await response.json();
  }

  /**
   * 渡された文字列データのSHA256ハッシュ値を取得して返します。
   * @param data ハッシュ値の計算対象
   * @returns SHA256ハッシュ値
   */
  async getSha256HashValue(data) {
    const encodedPayload = new TextEncoder().encode(data);
    const hash = await crypto.subtle.digest("SHA-256", encodedPayload);
    const hashArray = Array.from(new Uint8Array(hash));
    return hashArray.map((bytes) => bytes.toString(16).padStart(2, "0")).join("");
  }
}

export const httpClient = new HttpClient();
